/**
 * NovaSenhaPage (`/nova-senha`, RN-M1-003, V4) — troca da senha temporária no
 * primeiro acesso (convite ou seed do admin).
 *
 * O Cognito responde `NEW_PASSWORD_REQUIRED` ao primeiro login; esta tela conclui
 * o desafio. Para admin, o passo seguinte é o cadastro do TOTP (ADR-021). Erros
 * genéricos (RN-GLOBAL-004); nenhuma senha é logada.
 */

import { FormEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthActions } from '../contexts/AuthContext';
import { useWhiteLabel } from '../contexts/WhiteLabelContext';

interface LocationState {
  readonly from?: string;
}

/** Tamanho mínimo exigido pela política de senha do User Pool. */
const TAMANHO_MINIMO = 12;

export function NovaSenhaPage(): JSX.Element {
  const { definirNovaSenha } = useAuthActions();
  const navigate = useNavigate();
  const location = useLocation();
  const whiteLabel = useWhiteLabel();

  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const destino = (location.state as LocationState | null)?.from ?? '/escolas';

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErro(null);
    if (senha !== confirmacao) {
      setErro('As senhas não coincidem.');
      return;
    }
    setEnviando(true);
    try {
      const passo = await definirNovaSenha(senha);
      if (passo === 'mfa') {
        navigate('/mfa', { state: { from: destino } });
      } else if (passo === 'cadastro-mfa') {
        navigate('/cadastro-mfa', { state: { from: destino } });
      } else {
        navigate(destino, { replace: true });
      }
    } catch {
      setErro('Não foi possível definir a nova senha. Verifique se ela atende à política de senha.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="auth-page" style={{ ['--cor-primaria' as string]: whiteLabel.primaryColor }}>
      <form className="auth-card" onSubmit={onSubmit} aria-labelledby="nova-senha-title">
        <h1 id="nova-senha-title" className="auth-title">
          Defina sua senha
        </h1>
        <p className="auth-subtitle">
          Primeiro acesso: troque a senha temporária. Use ao menos {TAMANHO_MINIMO} caracteres, com
          letras maiúsculas, minúsculas, números e símbolos.
        </p>

        <label className="field">
          <span>Nova senha</span>
          <input
            autoFocus
            type="password"
            autoComplete="new-password"
            minLength={TAMANHO_MINIMO}
            required
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
          />
        </label>

        <label className="field">
          <span>Confirme a nova senha</span>
          <input
            type="password"
            autoComplete="new-password"
            minLength={TAMANHO_MINIMO}
            required
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
          />
        </label>

        {erro && (
          <p className="form-error" role="alert">
            {erro}
          </p>
        )}

        <button type="submit" className="btn btn-primary" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar senha'}
        </button>
      </form>
    </div>
  );
}
